import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { LanguageModel } from '../models/language.model';
import { ApiService } from './api.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private languageChangeSubject: Subject<string> = new Subject<string>();
  public languageChange$ = this.languageChangeSubject.asObservable();

  constructor(
    private api: ApiService,
    private settings: SettingsService,
    private translate: TranslateService) { }


  async init() {
    const savedLanguage = await this.settings.language();
    const user = await this.settings.user();
    const preferredLanguage = savedLanguage || user?.language;

    let availableLanguages: LanguageModel[] = [];
    try {
      availableLanguages = (await firstValueFrom(this.api.getLanguages()))
        ?.filter(language => !!language?.code) ?? [];
    } catch {
      // Translation startup must still work when the languages endpoint is
      // temporarily unavailable (for example while starting offline).
    }

    const supportedLanguage = availableLanguages.find(
      language => language.code === preferredLanguage
    )?.code;
    const defaultLanguage = availableLanguages.find(language => language.default)?.code;
    const language = supportedLanguage || defaultLanguage || availableLanguages[0]?.code || preferredLanguage || 'en';

    this.translate.setDefaultLang(language);
  }

  async setLanguage(language: string) {
    this.translate.setDefaultLang(language);
    await this.settings.language(language);
    this.languageChangeSubject.next(language);
  }

  getTranslation(key: string, defaultText: string) {
    try {
      const res = this.translate.instant(key);
      if (res == key)
        return defaultText;
      return res;
    } catch (error) {
      console.error('An error occurred:', error);
      return defaultText;
    }
  }

  translateObject(prefix: string, obj: any, suffix: string | null = null) {
    try {
      const translatedObj: { [key: string]: string } = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          if (typeof obj[key] === 'object') {
            translatedObj[key] = this.translateObject(`${prefix}-${key}`, obj[key]);
          } else if (typeof obj[key] === 'string') {
            let tk = `${prefix}.${key}`;
            if (suffix)
              tk = `${tk}-${suffix}`;
            translatedObj[key] = this.getTranslation(tk, obj[key]);
          } else {
            translatedObj[key] = obj[key];
          }
        }
      }
      return translatedObj;
    }
    catch (error) {
      console.error('An error occurred:', error);
      return obj;
    }
  }

  translateArray(key: string, arr: any[]) {
    try {
      const translatedArr = [];
      var i = 0;
      for (const obj of arr) {
        const translatedObj = this.translateObject(key, obj, `${i}`);
        translatedArr.push(translatedObj);
        i++;
      }
      return translatedArr;
    }
    catch (error) {
      console.error('An error occurred tanslating data:', error);
      return arr;
    }
  }
}
